<!-- eslint-disable vue/define-emits-declaration -->
<script setup lang="ts">
import { useResizeObserver, watchDebounced } from "@vueuse/core";
import { Dropdown, Menu } from "floating-vue";
import { type Component, computed, getCurrentInstance, inject, nextTick, onMounted, provide, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import MultiselectDropdownInner from "~/components/dumb/MultiselectDropdownInner.vue";
import MobileBottomSheet from "~/components/MobileBottomSheet.vue";
import { PARENT_ON_AFTER_CLOSE_FUNCTION, PARENT_ON_AFTER_OPEN_FUNCTION } from "~/constants/injection";
import { colorsByTheme } from "~/constants/style";
import { Placement, UserRole } from "~/shared/enums";
import type {
  MultiselectDropdownMenuItem,
  MultiselectDropdownMenuSection,
  ValidationFunctionResult,
} from "~/shared/types";
import { usePageStore, useUserStore } from "~/stores";

const props = defineProps<{
  container?: string;
  disabled?: boolean;
  sections: MultiselectDropdownMenuSection[];
  placement?: Placement;
  headerTitle?: string;
  placeholder?: string;
  newEntryIcon?: Component;
  newEntryIconArgs?: object;
  makeNewEntryText?: (search: string) => string;
  newEntryRoleOverride?: UserRole;
  inline?: boolean;
  block?: boolean;
  heightBlock?: boolean;
  distance?: number;
  skidding?: number;
  cover?: boolean;
  widthPixels?: number;
  maxItems?: number;
  closeOnSelect?: boolean;
  dontPropagateClose?: boolean;
  showOnHover?: boolean;
  itemClasses?: string[];
  propagateClickClasses?: string[];
  showRecommendationButton?: boolean;
  infiniteList?: boolean;
  loadingMore?: boolean;
  autoSize?: boolean;
  enableNewEntry?: boolean;
  showAll?: boolean;
  validate?: (value: string) => ValidationFunctionResult;
  onKeydown?: (event: KeyboardEvent, search: string) => void;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
  hasNewEntrySlot?: boolean;
}>();

const emit = defineEmits<{
  (e: "add", value: string | boolean | number | null): void;
  (e: "add", value: string): void;
  (e: "create", value: string | boolean | number | null): void;
  (e: "create", value: string): void;
  (e: "remove", value: string | boolean | number | null): void;
  (e: "remove", value: string): void;
  (e: "replace", value: string | boolean | number | null): void;
  (e: "replace", value: string): void;
  (e: "recommend"): void;
  (e: "search", value: string): void;
}>();

const currentInstance = getCurrentInstance();
const pageStore = usePageStore();
const userStore = useUserStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);
const isMobile = computed(() => pageStore.isMobile);

const contentRef = ref<InstanceType<typeof MultiselectDropdownInner> | null>(null);
const search = ref("");
const scrollContainer = computed<HTMLDivElement | null>(() => contentRef.value && contentRef.value.$el);
const mobileSheet = ref<ComponentExposed<typeof MobileBottomSheet<boolean>> | null>(null);

const currentSearch = computed(() => (isMobile.value ? (mobileSheet.value?.search ?? "") : search.value));

const newEntryValidationResult = computed(() =>
  props.enableNewEntry ? (props.validate?.(currentSearch.value) ?? { isValid: true, error: undefined }) : undefined
);
const newEntryIsValid = computed(() => newEntryValidationResult.value?.isValid);
const newEntryError = computed(() =>
  newEntryValidationResult.value && !newEntryValidationResult.value.isValid
    ? newEntryValidationResult.value.error
    : undefined
);

const placementDefinite = computed(() => props.placement ?? Placement.BOTTOM_LEFT);
const defaultDistance = computed(() => props.distance ?? (props.cover ? 0 : -8));
const calcDistance = ref(defaultDistance.value);

const clearSearch = () => {
  search.value = "";
  if (!isMobile.value) {
    contentRef.value?.clearSearch();
  } else {
    mobileSheet.value?.clearSearch();
  }
};

const filterItems = (items: MultiselectDropdownMenuItem[]) => {
  const searchLower = currentSearch.value.toLowerCase();

  const itemsToFilter = isMobile.value ? items : items.filter((item) => !item.selected || item.alwaysShowInList);

  let results = currentSearch.value
    ? itemsToFilter.filter(
        (item) =>
          item.label.toLowerCase().includes(searchLower) ||
          item.adtlSearchTerms?.some((term) => term && term.toLowerCase().includes(searchLower))
      )
    : itemsToFilter;

  if (isMobile.value) {
    results = results.sort((a, b) => {
      if (a.selected && !b.selected) {
        return -1;
      }
      if (!a.selected && b.selected) {
        return 1;
      }
      return 0;
    });
  }

  return results.slice(0, Number(props.maxItems ?? results.length)).map((item, index) => ({ ...item, index }));
};

const items = computed(() => props.sections.flatMap((section) => section.items));

const selectedItems = computed(() => items.value.filter((item) => item.selected));
const filteredSections = computed(() =>
  props.sections.map((section) => ({ ...section, items: filterItems(section.items) }))
);

const newEntryAllowed = computed(() => userStore.isRoleGte(props.newEntryRoleOverride ?? UserRole.ADMIN));

const contentProps = computed(() => ({
  filteredSections: filteredSections.value,
  items: items.value,
  search: isMobile.value ? mobileSheet.value?.search : search.value,
  placeholder: props.placeholder,
  itemClasses: props.itemClasses,
  enableNewEntry: props.enableNewEntry,
  newEntryIsValid: newEntryIsValid.value,
  newEntryError: newEntryError.value,
  newEntryAllowed: newEntryAllowed.value,
  newEntryIcon: props.newEntryIcon,
  newEntryIconArgs: props.newEntryIconArgs,
  makeNewEntryText: props.makeNewEntryText,
  hasNewEntrySlot: props.hasNewEntrySlot,
  infiniteList: props.infiniteList,
  loadingMore: props.loadingMore,
  showRecommendationButton: props.showRecommendationButton,
}));

const dropdownRef = ref<InstanceType<typeof Dropdown> | null>(null);
const wrapperRef = ref<HTMLDivElement | null>(null);
const recalculateDistance = () => {
  calcDistance.value = defaultDistance.value;
  if (!wrapperRef.value || !props.cover) {
    return;
  }
  calcDistance.value -= wrapperRef.value.getBoundingClientRect().height;
};
const assignWrapperRef = (elem: HTMLDivElement | null) => {
  wrapperRef.value = elem;
  recalculateDistance();
};
useResizeObserver(wrapperRef, recalculateDistance);
watch(() => props.cover, recalculateDistance);
watch(defaultDistance, recalculateDistance);

const isOpen = ref(false);
const isHidden = ref(false);

const parentOnAfterOpen = inject(PARENT_ON_AFTER_OPEN_FUNCTION);
const parentOnAfterClose = inject(PARENT_ON_AFTER_CLOSE_FUNCTION);

provide(PARENT_ON_AFTER_OPEN_FUNCTION, () => {
  if (!isMobile.value) {
    return;
  }
  isHidden.value = true;
});
provide(PARENT_ON_AFTER_CLOSE_FUNCTION, () => {
  if (!isMobile.value) {
    return;
  }
  isHidden.value = false;
});

const close = () => {
  if (isMobile.value) {
    isOpen.value = false;
    contentRef.value?.clearExpandedSections();
    parentOnAfterClose?.();
    props.onAfterClose?.();
  } else {
    dropdownRef.value?.hide();
  }
};

const onSelect = (item: MultiselectDropdownMenuItem) => {
  if (item.disabled) {
    return;
  }

  if (item.selected) {
    emit("remove", item.value);
  } else {
    emit("add", item.value);
  }
  clearSearch();
  if (!isMobile.value) {
    contentRef.value?.focusSearch();
  }
  if (props.closeOnSelect) {
    close();
  }
};

const onCreate = () => {
  if (!props.enableNewEntry || !newEntryIsValid.value) {
    return;
  }

  emit("create", currentSearch.value);
  clearSearch();
  if (!isMobile.value) {
    contentRef.value?.focusSearch();
  }
  if (props.closeOnSelect) {
    close();
  }
};

// Emit remove event when user hits backspace with no search value
const onBackspace = (event: KeyboardEvent) => {
  if (search.value !== "") {
    return;
  }

  const lastSelectedItem = selectedItems.value[selectedItems.value.length - 1];
  if (!lastSelectedItem) {
    return;
  }

  emit("remove", lastSelectedItem.value);
  event.stopPropagation();
};

const moveUpOrDown = (isUp: boolean): boolean => {
  const allRows = contentRef.value?.getAllRows() || [];
  const currentIndex = allRows.findIndex((row) => row.isFocused);

  const newIndex = isUp ? currentIndex - 1 : currentIndex + 1;

  if (newIndex < -1 || newIndex >= allRows.length) {
    return false;
  }

  if (newIndex === -1) {
    contentRef.value?.focusSearch();
    allRows.forEach((row) => row.element?.blur());
  } else {
    allRows[newIndex].element?.focus();
  }

  return true;
};

const onKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value || isMobile.value) {
    return;
  }
  const { key } = event;
  const filteredItems = filteredSections.value.flatMap((section) => section.items);

  props.onKeydown?.(event, search.value);

  switch (key) {
    case "Shift":
    case "Meta":
    case "Control":
    case "Option":
    case "Alt": {
      break;
    }
    case "Enter": {
      if (filteredItems.length >= 1) {
        onSelect(filteredItems[0]);
      }
      onCreate();
      break;
    }
    case "Escape": {
      event.preventDefault();
      close();
      break;
    }
    case "ArrowUp":
    case "ArrowDown": {
      const success = moveUpOrDown(key === "ArrowUp");
      if (success) {
        event.preventDefault();
      }
      break;
    }
    case "Tab": {
      event.preventDefault();
      moveUpOrDown(event.shiftKey);
      break;
    }
    default: {
      if (document.activeElement?.tagName === "INPUT") {
        break;
      }
      contentRef.value?.focusSearch();
      break;
    }
  }
};

const updateSearch = (value: string) => {
  search.value = value;
};

watch(currentSearch, (value) => !props.infiniteList && emit("search", value));
watchDebounced(currentSearch, (value) => props.infiniteList && emit("search", value), { debounce: 500, maxWait: 1000 });

const onShow = () => {
  isOpen.value = true;
  search.value = "";
  props.onAfterOpen?.();

  if (!isMobile.value) {
    nextTick(() => contentRef.value?.focusSearch());
  }
};

const onHide = () => {
  isOpen.value = false;
  contentRef.value?.clearExpandedSections();
  props.onAfterClose?.();
};

const onMouseDown = (event: MouseEvent) => {
  if (!props.showOnHover) {
    return;
  }
  event.preventDefault();
};

const open = () => {
  if (isMobile.value && !props.disabled) {
    isOpen.value = true;
    parentOnAfterOpen?.();
    props.onAfterOpen?.();
  } else {
    dropdownRef.value?.show();
  }
};

const closeAndPropagateMaybe = (event: MouseEvent) => {
  close();
  const { clientX, clientY } = event;
  props.propagateClickClasses
    ?.flatMap((e) => Array.from(document.getElementsByClassName(e)))
    .filter((e): e is HTMLElement => e instanceof HTMLElement)
    .forEach((elem) => {
      const { left, top, width, height } = elem.getBoundingClientRect();
      if (!(clientX >= left && clientX <= left + width && clientY >= top && clientY <= top + height)) {
        return;
      }
      elem.click();
    });
};

onMounted(() => {
  if (!currentInstance) {
    return;
  }
  const instanceElem = currentInstance.subTree.el;
  if (!instanceElem?.children) {
    return;
  }

  assignWrapperRef(instanceElem.children[0] as HTMLDivElement);
});

defineExpose({
  open,
  close,
  isOpen,
  scrollContainer,
});
</script>

<template>
  <Dropdown
    v-if="!isMobile"
    ref="dropdownRef"
    :container="container"
    :disabled="disabled"
    :triggers="showOnHover ? ['hover', 'focus'] : undefined"
    :popper-triggers="showOnHover ? ['hover', 'focus'] : undefined"
    :placement="placementDefinite"
    :distance="calcDistance"
    :skidding="skidding"
    :dont-propagate-close="dontPropagateClose"
    :auto-size="autoSize"
    no-auto-focus
    :theme="`dropdown-${pageStore.theme}`"
    :class="{
      flex: !inline,
      inline: !!inline,
      'max-w-full grow': block,
      'h-full': heightBlock,
      'cursor-pointer': !disabled,
    }"
    @apply-show="onShow"
    @apply-hide="onHide">
    <template #default>
      <slot />
    </template>
    <template #popper>
      <Menu no-auto-focus @mousedown="onMouseDown">
        <MultiselectDropdownInner
          ref="contentRef"
          v-bind="contentProps"
          :block="props.block"
          :auto-size="props.autoSize"
          :width-pixels="props.widthPixels"
          :colors="colors"
          :show-all="showAll"
          @select="onSelect"
          @remove="emit('remove', $event)"
          @create="onCreate"
          @replace="emit('replace', $event)"
          @update-search="updateSearch"
          @recommend="emit('recommend')"
          @keydown="onKeydown"
          @backspace="onBackspace">
          <template #new-entry-slot>
            <slot name="new-entry-slot" />
          </template>
        </MultiselectDropdownInner>
        <Teleport v-if="isOpen" to="body">
          <div class="absolute inset-0" @click="closeAndPropagateMaybe" @keydown.escape="close" />
        </Teleport>
      </Menu>
    </template>
  </Dropdown>

  <template v-else>
    <div
      v-bind="$attrs"
      class="flex"
      :class="[
        $attrs.class,
        {
          inline: !!inline,
          'max-w-full grow': block,
          'h-full': heightBlock,
          'cursor-pointer': !disabled,
        },
      ]"
      @click="open"
      @keydown="open">
      <slot />
    </div>

    <MobileBottomSheet
      ref="mobileSheet"
      :entity="isOpen"
      :hidden="isHidden"
      :title="headerTitle"
      include-done
      include-search
      @close="close">
      <MultiselectDropdownInner
        ref="contentRef"
        v-bind="contentProps"
        :block="false"
        auto-size
        :width-pixels="undefined"
        :colors="undefined"
        :show-all="showAll"
        @select="onSelect"
        @remove="emit('remove', $event)"
        @create="onCreate"
        @replace="emit('replace', $event)"
        @update-search="updateSearch"
        @recommend="emit('recommend')"
        @keydown="onKeydown"
        @backspace="onBackspace">
        <template #new-entry-slot>
          <slot name="new-entry-slot" />
        </template>
      </MultiselectDropdownInner>
    </MobileBottomSheet>
  </template>
</template>

<style>
.v-popper--theme-dropdown .v-popper__arrow-container {
  display: none;
}
.v-popper--theme-dropdown .v-popper__wrapper {
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.v-popper--theme-dropdown .v-popper__inner {
  border-width: 0px;
  border-radius: 4px;
  background-color: transparent;
}
</style>
